import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { setVendorSubUserError } from 'slices/vendorSubUserSlice';
import { CustomSwitch } from 'components/common/Switch';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import ErrorMessage from 'components/common/ErrorMessage';
import { Controller, useForm } from 'react-hook-form';
import { styled } from '@mui/material';
import { Dialog, DialogTitle } from '@mui/material';
import PhoneInput from 'components/common/PhoneInput';
import { Select } from 'components/common/Select';
import {
  vendorSubUserSelector,
  fetchVendorSubUsers,
  addVendorSubUser,
  updateVendorSubUser
} from 'slices/vendorSubUserSlice';

const VendorSubUsersForm = ({ item, handleClose, callback, show }) => {
  const dispatch = useDispatch();
  const { getValues, handleSubmit, watch, formState: { errors }, control, setValue, clearErrors } = useForm();
  const [status, setStatus] = useState(1)
  const { error, loader } = useSelector(vendorSubUserSelector);

  const clearAll = () => {
    setValue("name", '');
    setValue('gender', '');
    setValue('email', '');
    setValue('password', '');
    setValue('phone', '');
    setStatus(1)
    dispatch(setVendorSubUserError({}))
  }

  useEffect(() => {
    setValue('name', item?.user?.name || '');
    setValue('gender', item?.gender || '');
    setValue('email', item?.user?.email || '');
    setValue('phone', item?.phone || '');
    setStatus(typeof (item?.status) === "number" ? item?.status : 1);
  }, [item]);

  useEffect(() => {
    return () => clearAll()
  }, [show])

  const submitCallback = (success, message) => {
    if (success) {
      callback();
      toast.success(message, { className: "successToast" });
      dispatch(fetchVendorSubUsers());
      handleClose();
    } else {
      toast.error(message, { className: "errorToast" });
    }
  };

  const onSubmit = (data) => {
    dispatch(setVendorSubUserError({}));
    data = { ...data, status };
    if (item?.id) {
      dispatch(updateVendorSubUser(data, item?.id, submitCallback));
    } else {
      dispatch(addVendorSubUser(data, submitCallback));
    }
  }

  const GenderOptions = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
    { label: "Nonbinary", value: "nonbinary" }
  ];

  const onError = (errors) => console.log("errors", errors);

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        {/* Name */}
        <div className="row-primary">
          <h2>Sub User Name *</h2>
          <Controller
            name="name"
            rules={{ required: "Please enter name" }}
            control={control}
            render={({ field }) => (
              <div className='w-100'>
                <input className="input-in type_inp" type="text" placeholder='Sub user name' {...field} />
                {errors?.name?.message && <ErrorMessage text={errors?.name?.message} />}
              </div>
            )}
          />
        </div>

        {/* Gender */}
        <div className="row-primary">
          <h2>Gender *</h2>
          <Controller
            name="gender"
            control={control}
            rules={{ required: "Please select a gender" }}
            render={({ field }) => (
              <div className='select_box' style={{ width: "100%" }}>
                <Select
                  name="gender"
                  options={GenderOptions}
                  value={GenderOptions.find(opt => opt.value === field.value) || ""}
                  isClearable={false}
                  onChange={(value) => field.onChange(value)}
                />
                {errors?.gender?.message && <ErrorMessage text={errors?.gender?.message} />}
              </div>
            )}
          />
        </div>

        {/* Email */}
        <div className="row-primary">
          <h2>Email *</h2>
          <Controller
            name="email"
            rules={{ required: 'Email field is required' }}
            control={control}
            render={({ field }) => (
              <div style={{ width: "100%" }}>
                <input className="input-in type_inp" type="email" placeholder='Email address' {...field} />
                {errors?.email?.message && <ErrorMessage text={errors?.email?.message} />}
                {error?.errors?.email?.length > 0 &&
                  <ErrorMessage text={error?.errors?.email?.[0]} />}
              </div>
            )}
          />
        </div>

        {/* Password */}
        <div className="row-primary">
          <h2>Password {item ? '' : '*'}</h2>
          <Controller
            name="password"
            control={control}
            rules={item ? {} : { required: "Password required" }}
            render={({ field }) => (
              <input className="input-in type_inp" type="password" placeholder="Password" {...field} />
            )}
          />
          {errors?.password?.message && <ErrorMessage text={errors?.password?.message} />}
        </div>

        {/* Phone */}
        <div className="row-primary">
          <h2>Phone *</h2>
          <Controller
            name="phone"
            control={control}
            rules={{ required: "Please enter phone" }}
            render={({ field }) => (
              <PhoneInput className="input-in type_inp" placeholder='Phone number' {...field} />
            )}
          />
          {errors?.phone?.message && <ErrorMessage text={errors?.phone?.message} />}
        </div>

        {/* Status */}
        <div className="row-primary align-items-center">
          <h2 className='mt-0'>Status</h2>
          <CustomSwitch
            labelText="active"
            checked={status === 1}
            handleChange={(checked) => setStatus(checked ? 1 : 0)}
          />
        </div>

        {/* Error messages */}
        {error?.errors?.name?.map((err, i) => (
          <ErrorMessage key={i} text={err} />
        ))}

        {/* Footer */}
        <div className="Form-footer">
          <button type="button" className="Cancle_button" onClick={handleClose}>Cancel</button>
          <SubmitButton btnText={item?.id ? "Update" : "Create"} loader={loader} />
        </div>
      </form>
    </div>
  )
}

// ✅ Bootstrap Dialog Wrapper
export const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    overflowY: "auto",
    margin: "auto",
    alignItems: "flex-start",
  },
  '& .MuiPaper-root': {
    maxHeight: "none",
    overflowY: "hidden",
    borderRadius: 20
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const AddVendorSubUsers = ({ item, onClose, callback, show }) => {
  return (
    <BootstrapDialog
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={show}
      sx={{
        '& .MuiDialog-container': { alignItems: "flex-start" },
        '& .MuiPaper-root': { overflowY: "visible" },
      }}
    >
      <div className="modal-content full_modal">
        <DialogTitle>
          <div className="modal-header header_modal align-items-center">
            <div></div>
            <h5 className="modal-title_u">{item?.id ? "Update" : "Add"} Vendor Sub User</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => onClose(item?.id, item?.status)}></button>
          </div>
        </DialogTitle>
        <div className="modal-body modal_form_body">
          <VendorSubUsersForm item={item} handleClose={onClose} callback={callback} show={show} />
        </div>
      </div>
    </BootstrapDialog>
  )
}

export default AddVendorSubUsers;
